import { useCallback, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const ENDPOINT = () => `${API_URL}/v1/admin/ai/test_runs`;

// Steps arrive from the server numbered from 1 per request. Continuing or re-running a run
// returns a fresh trace, so its ids are renumbered before being appended or the new steps would
// collide with the existing ones and selection would jump to the wrong row.
function appendSteps(existing, incoming) {
  const reruns = existing.filter((s) => s.type === 'answer').length;
  return incoming.map((step, index) => ({
    ...step,
    id: `step-${existing.length + index + 1}`,
    label: step.type === 'answer' && reruns > 0 ? `${step.label} (re-run ${reruns + 1})` : step.label,
  }));
}

export default function useRagTestRun() {
  const isMounted = useIsMounted();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const post = useCallback(async (body) => {
    const res = await fetchWithAuth(ENDPOINT(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload.errors?.join(', ') || payload.error || res.statusText || 'Test run failed');
    }
    return res.json();
  }, []);

  const runTest = useCallback(async ({ assetId, messages, mode }) => {
    setLoading(true);
    setError(null);
    try {
      setRun(await post({ asset_id: assetId, messages, mode }));
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Test run failed');
      setRun(null);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [post, isMounted]);

  // Generates an answer from a fragment set the run already has, and appends the new steps
  // rather than replacing the run — so a second answer sits beside the first and the two can be
  // read against each other.
  const answerFromFragments = useCallback(async ({ assetId, messages, fragmentIds }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await post({
        asset_id: assetId, messages, mode: 'answer', fragment_ids: fragmentIds,
      });
      let appendedFirstId = null;
      setRun((current) => {
        const existing = current?.steps || [];
        const added = appendSteps(existing, result.steps || []);
        appendedFirstId = added[0]?.id ?? null;
        return { ...(current || result), steps: [...existing, ...added] };
      });
      return appendedFirstId;
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Test run failed');
      return null;
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [post, isMounted]);

  const reset = useCallback(() => {
    setRun(null);
    setError(null);
  }, []);

  return { run, loading, error, runTest, answerFromFragments, reset };
}
